import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../contexts/FamilyContext'
import { useAuth } from '../contexts/AuthContext'
import Layout from '../components/layout/Layout'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../components/ui/Button'
import TaskCard from '../components/tasks/TaskCard'
import CreateTaskModal from '../components/tasks/CreateTaskModal'
import EditTaskModal from '../components/tasks/EditTaskModal'
import DeleteTaskModal from '../components/tasks/DeleteTaskModal'
import TaskTemplateModal from '../components/tasks/TaskTemplateModal'
import QuickTaskSuggestions from '../components/tasks/QuickTaskSuggestions'
import Badge from '../components/ui/Badge'
import Card from '../components/ui/Card'

const { FiPlus, FiFilter, FiSearch, FiClock, FiTimer, FiBookOpen, FiZap } = FiIcons

const TasksPage = () => {
  const { tasks, loading } = useFamily()
  const { family }  = useAuth()
  const [showCreateTask, setShowCreateTask] = useState(false)
  const [showEditTask, setShowEditTask] = useState(false)
  const [showDeleteTask, setShowDeleteTask] = useState(false)
  const [showTaskTemplates, setShowTaskTemplates] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedChild, setSelectedChild] = useState('')
  const [timeFilter, setTimeFilter] = useState('all')
  const [showSuggestions, setShowSuggestions] = useState(tasks.length === 0)

  const handleEditTask = (task) => {
    setSelectedTask(task)
    setShowEditTask(true)
  }

  const handleDeleteTask = (task) => {
    setSelectedTask(task)
    setShowDeleteTask(true)
  }

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filter === 'all' || task.category === filter
    
    const matchesChild = !selectedChild || task.assigned_to === selectedChild
    
    const matchesTime = (() => {
      if (timeFilter === 'all') return true
      if (!task.estimated_time_minutes) return timeFilter === 'unknown'
      
      switch (timeFilter) {
        case 'quick':
          return task.estimated_time_minutes <= 10
        case 'medium':
          return task.estimated_time_minutes > 10 && task.estimated_time_minutes <= 30
        case 'long':
          return task.estimated_time_minutes > 30
        case 'unknown':
          return false
        default:
          return true
      }
    })()

    return matchesSearch && matchesFilter && matchesChild && matchesTime
  })

  const categories = [
    { value: 'all', label: 'All Tasks', count: tasks.length },
    { value: 'cleaning', label: 'Cleaning', count: tasks.filter(t => t.category === 'cleaning').length },
    { value: 'dishes', label: 'Dishes', count: tasks.filter(t => t.category === 'dishes').length },
    { value: 'laundry', label: 'Laundry', count: tasks.filter(t => t.category === 'laundry').length },
    { value: 'pets', label: 'Pet Care', count: tasks.filter(t => t.category === 'pets').length },
    { value: 'homework', label: 'Homework', count: tasks.filter(t => t.category === 'homework').length },
    { value: 'outdoor', label: 'Outdoor', count: tasks.filter(t => t.category === 'outdoor').length },
    { value: 'cooking', label: 'Cooking', count: tasks.filter(t => t.category === 'cooking').length },
    { value: 'organization', label: 'Organization', count: tasks.filter(t => t.category === 'organization').length },
    { value: 'other', label: 'Other', count: tasks.filter(t => t.category === 'other').length }
  ]

  const timeFilters = [
    { value: 'all', label: 'All Times', count: tasks.length },
    { value: 'quick', label: 'Quick (≤10 min)', count: tasks.filter(t => t.estimated_time_minutes && t.estimated_time_minutes <= 10).length },
    { value: 'medium', label: 'Medium (11-30 min)', count: tasks.filter(t => t.estimated_time_minutes && t.estimated_time_minutes > 10 && t.estimated_time_minutes <= 30).length },
    { value: 'long', label: 'Long (>30 min)', count: tasks.filter(t => t.estimated_time_minutes && t.estimated_time_minutes > 30).length },
    { value: 'unknown', label: 'No Time Set', count: tasks.filter(t => !t.estimated_time_minutes).length }
  ]

  const getTimeStats = () => {
    const tasksWithTime = tasks.filter(t => t.estimated_time_minutes)
    if (tasksWithTime.length === 0) return null

    const totalMinutes = tasksWithTime.reduce((sum, task) => sum + task.estimated_time_minutes, 0)
    const averageTime = Math.round(totalMinutes / tasksWithTime.length)
    const shortestTime = Math.min(...tasksWithTime.map(t => t.estimated_time_minutes))
    const longestTime = Math.max(...tasksWithTime.map(t => t.estimated_time_minutes))

    return { totalMinutes, averageTime, shortestTime, longestTime, taskCount: tasksWithTime.length }
  }

  const formatTimeDisplay = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  const timeStats = getTimeStats()

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading tasks...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Family Tasks</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage and track all household chores
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowTaskTemplates(true)}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={FiBookOpen} />
              Task Library
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowCreateTask(true)}
            >
              <SafeIcon icon={FiPlus} />
              Create Custom Task
            </Button>
          </div>
        </div>

        {/* Show suggestions for new users or toggle */}
        {(showSuggestions || tasks.length === 0) && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {tasks.length === 0 ? 'Get Started with These Popular Tasks' : 'Task Suggestions'}
              </h2>
              {tasks.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSuggestions(!showSuggestions)}
                >
                  {showSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
                </Button>
              )}
            </div>
            <QuickTaskSuggestions maxSuggestions={6} />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Child Filter */}
            {family?.children && family.children.length > 0 && (
              <div>
                <select
                  value={selectedChild}
                  onChange={(e) => setSelectedChild(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">All Children</option>
                  {family.children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Time Filter */}
            <div>
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                {timeFilters.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label} ({filter.count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setFilter(category.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  filter === category.value
                    ? 'bg-primary-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category.label}
                {category.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    filter === category.value
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                  }`}>
                    {category.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Time Statistics */}
        {timeStats && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiTimer} className="text-blue-500 dark:text-blue-400" />
              Time Overview
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatTimeDisplay(timeStats.totalMinutes)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Time Available</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatTimeDisplay(timeStats.averageTime)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Average Task Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatTimeDisplay(timeStats.shortestTime)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Quickest Task</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatTimeDisplay(timeStats.longestTime)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Longest Task</div>
              </div>
            </div>
          </div>
        )}

        {/* Add toggle for suggestions if tasks exist */}
        {tasks.length > 0 && !showSuggestions && (
          <div className="mb-6 text-center">
            <Button
              variant="outline"
              onClick={() => setShowSuggestions(true)}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={FiZap} />
              Show Task Suggestions
            </Button>
          </div>
        )}

        {/* Tasks Grid */}
        {filteredTasks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTasks.map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <TaskCard
                  task={task}
                  view="parent"
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {searchTerm || filter !== 'all' || selectedChild || timeFilter !== 'all'
                ? 'No tasks match your filters'
                : 'No tasks created yet'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm || filter !== 'all' || selectedChild || timeFilter !== 'all'
                ? 'Try adjusting your search or filters to find tasks.'
                : 'Create your first task to get started with family chore management.'}
            </p>
            {(!searchTerm && filter === 'all' && !selectedChild && timeFilter === 'all') && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={() => setShowTaskTemplates(true)}
                >
                  <SafeIcon icon={FiBookOpen} />
                  Browse Task Library
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateTask(true)}
                >
                  <SafeIcon icon={FiPlus} />
                  Create Custom Task
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Task Statistics */}
        {tasks.length > 0 && (
          <div className="mt-12 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Task Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{tasks.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Tasks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                  {tasks.filter(t => t.recurring_pattern === 'daily').length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Daily Tasks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">
                  {tasks.filter(t => t.recurring_pattern === 'weekly').length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Weekly Tasks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {tasks.reduce((sum, task) => sum + task.points, 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Points Available</div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions Panel */}
        {tasks.length > 0 && (
          <Card className="mt-8 p-6 bg-gradient-to-r from-highlight-50 to-accent-50 dark:from-highlight-900/20 dark:to-accent-900/20">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Quick Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTaskTemplates(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiBookOpen} />
                Browse 100+ Task Templates
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSuggestions(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiZap} />
                Get Task Suggestions
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateTask(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiPlus} />
                Create Custom Task
              </Button>
            </div>
          </Card>
        )}

        {/* Modals */}
        <CreateTaskModal
          isOpen={showCreateTask}
          onClose={() => setShowCreateTask(false)}
        />
        
        <TaskTemplateModal
          isOpen={showTaskTemplates}
          onClose={() => setShowTaskTemplates(false)}
        />

        <EditTaskModal
          isOpen={showEditTask}
          onClose={() => {
            setShowEditTask(false)
            setSelectedTask(null)
          }}
          task={selectedTask}
        />

        <DeleteTaskModal
          isOpen={showDeleteTask}
          onClose={() => {
            setShowDeleteTask(false)
            setSelectedTask(null)
          }}
          task={selectedTask}
        />
      </div>
    </Layout>
  )
}

export default TasksPage